import type Phaser from 'phaser';
import { findTrack, SYNTH_BPM } from '@core/config/music';

/** Forma de una nota sintetizada. */
interface Tone {
  readonly frequency: number;
  readonly durationMs: number;
  readonly type: OscillatorType;
}

const MASTER_VOLUME = 0.18;
const MUSIC_VOLUME = 0.08;
const MUSIC_FILE_VOLUME = 0.4;
const MUSIC_BEAT_MS = 270;
const MUTE_STORAGE_KEY = 'maia-eyekon:muted';

/** Melodia de fondo (pentatonica mayor de Do, alegre) en corcheas, en bucle. */
const MUSIC_MELODY: readonly number[] = [
  659, 784, 880, 784, 659, 587, 659, 523, 587, 659, 784, 659, 587, 523, 440, 523,
];

/** Notas graves de acompanamiento, una por compas (cada 4 pasos). */
const MUSIC_BASS: readonly number[] = [131, 131, 165, 196];

/** Estado de la musica de fondo: nada, sintetizada, o la clave de una pista de archivo. */
type MusicState = 'none' | 'synth' | string;

/**
 * Servicio de sonido del juego. Los efectos (clicks, aciertos, etc.) se generan por codigo
 * con la Web Audio API para mantener el juego liviano. La musica de fondo puede ser:
 *   - una pista real cargada desde un archivo (si existe para esa escena), o
 *   - una melodia generada por codigo como fallback (asi nunca hay silencio).
 * Los navegadores exigen un gesto del usuario para reproducir audio, por eso todo arranca
 * recien al llamar a unlock() (en el primer toque/click).
 */
export class AudioService {
  private context: AudioContext | null = null;
  private muted = false;
  private unlocked = false;

  // --- Musica sintetizada (fallback) ---
  private musicGain: GainNode | null = null;
  private musicTimer: number | null = null;
  private musicStep = 0;

  // --- Musica desde archivos (via el SoundManager de Phaser) ---
  private soundManager: Phaser.Sound.BaseSoundManager | null = null;
  private availableTracks = new Set<string>();
  private fileMusic: Phaser.Sound.BaseSound | null = null;

  /** Que esta sonando ahora mismo. */
  private nowPlaying: MusicState = 'none';
  /** Que deberia sonar (clave de pista o null = sintetizada). */
  private desiredKey: string | null = null;
  /** El jugador quiere musica (se respeta al mutear/desmutear y cambiar de escena). */
  private musicWanted = false;

  public constructor() {
    this.muted = this.loadMuted();
  }

  public get isMuted(): boolean {
    return this.muted;
  }

  /** Conecta el SoundManager de Phaser para poder reproducir pistas de archivo. */
  public attachSound(manager: Phaser.Sound.BaseSoundManager): void {
    this.soundManager = manager;
  }

  /** Define que pistas de archivo se cargaron correctamente (las informa el Preload). */
  public setAvailableTracks(keys: Iterable<string>): void {
    this.availableTracks = new Set(keys);
  }

  /** Habilita el audio tras el primer gesto del usuario y arranca la musica deseada. */
  public unlock(): void {
    if (this.unlocked) {
      return;
    }
    this.unlocked = true;
    this.musicWanted = true;
    this.applyMusic();
  }

  /**
   * Pide la musica correspondiente a una escena. Recibe la clave de pista mapeada para esa
   * escena (o undefined si la escena no cambia la musica). Si la pista no tiene archivo,
   * usa la melodia sintetizada como fallback.
   */
  public playSceneMusic(mappedKey: string | undefined): void {
    if (mappedKey === undefined) {
      return;
    }
    this.musicWanted = true;
    this.desiredKey = this.availableTracks.has(mappedKey) ? mappedKey : null;
    this.applyMusic();
  }

  /** Duracion de un pulso (negra) en ms para la pista actual; sirve para sincronizar Ritmo. */
  public getMusicBeatMs(): number {
    if (this.nowPlaying !== 'none' && this.nowPlaying !== 'synth') {
      const track = findTrack(this.nowPlaying);
      if (track !== undefined) {
        return 60_000 / track.bpm;
      }
    }
    return 60_000 / SYNTH_BPM;
  }

  public toggleMute(): boolean {
    this.muted = !this.muted;
    this.persistMuted();

    if (this.muted) {
      this.stopAllMusic();
    } else {
      this.applyMusic();
    }
    return this.muted;
  }

  /** Decide y aplica que musica deberia estar sonando segun el estado actual. */
  private applyMusic(): void {
    if (this.muted || !this.unlocked || !this.musicWanted) {
      return;
    }
    const target: MusicState = this.desiredKey ?? 'synth';
    if (this.nowPlaying === target) {
      return;
    }

    this.stopAllMusic();

    if (this.desiredKey !== null && this.canPlayFile(this.desiredKey)) {
      this.startFileMusic(this.desiredKey);
      this.nowPlaying = this.desiredKey;
    } else {
      this.runSynthMusic();
      this.nowPlaying = 'synth';
    }
  }

  private canPlayFile(key: string): boolean {
    return this.soundManager !== null && this.availableTracks.has(key);
  }

  private startFileMusic(key: string): void {
    if (this.soundManager === null) {
      return;
    }
    this.fileMusic = this.soundManager.add(key, { loop: true, volume: MUSIC_FILE_VOLUME });
    this.fileMusic.play();
  }

  private stopFileMusic(): void {
    if (this.fileMusic !== null) {
      this.fileMusic.stop();
      this.fileMusic.destroy();
      this.fileMusic = null;
    }
  }

  private stopAllMusic(): void {
    this.stopFileMusic();
    this.stopSynthMusic();
    this.nowPlaying = 'none';
  }

  public playClick(): void {
    this.playSequence([{ frequency: 520, durationMs: 70, type: 'triangle' }]);
  }

  public playHit(): void {
    this.playSequence([{ frequency: 660, durationMs: 80, type: 'sine' }]);
  }

  public playMiss(): void {
    this.playSequence([{ frequency: 180, durationMs: 140, type: 'sawtooth' }]);
  }

  public playCorrect(): void {
    this.playSequence([
      { frequency: 660, durationMs: 90, type: 'sine' },
      { frequency: 880, durationMs: 120, type: 'sine' },
    ]);
  }

  public playWrong(): void {
    this.playSequence([
      { frequency: 220, durationMs: 120, type: 'square' },
      { frequency: 160, durationMs: 160, type: 'square' },
    ]);
  }

  public playReward(): void {
    this.playSequence([
      { frequency: 523, durationMs: 90, type: 'triangle' },
      { frequency: 659, durationMs: 90, type: 'triangle' },
      { frequency: 784, durationMs: 140, type: 'triangle' },
    ]);
  }

  /** Fanfarria ascendente para celebrar un record o una mision cumplida. */
  public playFanfare(): void {
    this.playSequence([
      { frequency: 523, durationMs: 110, type: 'triangle' },
      { frequency: 659, durationMs: 110, type: 'triangle' },
      { frequency: 784, durationMs: 110, type: 'triangle' },
      { frequency: 1047, durationMs: 230, type: 'triangle' },
      { frequency: 988, durationMs: 90, type: 'triangle' },
      { frequency: 1047, durationMs: 300, type: 'triangle' },
    ]);
  }

  private runSynthMusic(): void {
    const context = this.ensureContext();
    if (context === null || this.musicTimer !== null) {
      return;
    }

    this.musicGain = context.createGain();
    this.musicGain.gain.setValueAtTime(MUSIC_VOLUME, context.currentTime);
    this.musicGain.connect(context.destination);

    this.musicStep = 0;
    this.musicTimer = window.setInterval(() => this.tickMusic(context), MUSIC_BEAT_MS);
  }

  private stopSynthMusic(): void {
    if (this.musicTimer !== null) {
      window.clearInterval(this.musicTimer);
      this.musicTimer = null;
    }
    if (this.musicGain !== null) {
      this.musicGain.disconnect();
      this.musicGain = null;
    }
  }

  private tickMusic(context: AudioContext): void {
    if (this.muted || this.musicGain === null) {
      return;
    }

    const step = this.musicStep;
    const note = MUSIC_MELODY[step % MUSIC_MELODY.length];
    this.scheduleMusicTone(context, note, 'triangle', 230, 0.6);

    if (step % 4 === 0) {
      const bass = MUSIC_BASS[Math.floor(step / 4) % MUSIC_BASS.length];
      this.scheduleMusicTone(context, bass, 'sine', 620, 0.7);
    }

    this.musicStep = (step + 1) % (MUSIC_MELODY.length * 4);
  }

  private scheduleMusicTone(
    context: AudioContext,
    frequency: number,
    type: OscillatorType,
    durationMs: number,
    peak: number,
  ): void {
    if (this.musicGain === null) {
      return;
    }

    const now = context.currentTime;
    const endTime = now + durationMs / 1000;

    const oscillator = context.createOscillator();
    const gain = context.createGain();

    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, now);

    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(peak, now + 0.04);
    gain.gain.exponentialRampToValueAtTime(0.0001, endTime);

    oscillator.connect(gain);
    gain.connect(this.musicGain);

    oscillator.start(now);
    oscillator.stop(endTime + 0.02);
  }

  private loadMuted(): boolean {
    try {
      return window.localStorage.getItem(MUTE_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  }

  private persistMuted(): void {
    try {
      window.localStorage.setItem(MUTE_STORAGE_KEY, String(this.muted));
    } catch {
      // Almacenamiento no disponible: se ignora silenciosamente.
    }
  }

  private ensureContext(): AudioContext | null {
    if (this.context === null) {
      try {
        this.context = new AudioContext();
      } catch (error) {
        console.error('Web Audio API no disponible en este navegador.', error);
        return null;
      }
    }
    if (this.context.state === 'suspended') {
      void this.context.resume();
    }
    return this.context;
  }

  private playSequence(tones: Tone[]): void {
    if (this.muted) {
      return;
    }
    const context = this.ensureContext();
    if (context === null) {
      return;
    }

    let startTime = context.currentTime;
    for (const tone of tones) {
      this.scheduleTone(context, tone, startTime);
      startTime += tone.durationMs / 1000;
    }
  }

  private scheduleTone(context: AudioContext, tone: Tone, startTime: number): void {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const endTime = startTime + tone.durationMs / 1000;

    oscillator.type = tone.type;
    oscillator.frequency.setValueAtTime(tone.frequency, startTime);

    gain.gain.setValueAtTime(MASTER_VOLUME, startTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, endTime);

    oscillator.connect(gain);
    gain.connect(context.destination);

    oscillator.start(startTime);
    oscillator.stop(endTime);
  }
}
